import React, { useState, useEffect, useRef, useMemo } from "react";
import { FormControlLabel, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LoadingSpinner } from "ctech-react-components";
import { createProvider, updateProvider } from "services/paymentApi";
import Billing from "services/billingApi";
import { getErrorMessage } from "../../utils/errorHandler";
import { PAYMENT_METHOD_ORDER } from "../../constants/paymentMethods";
import { sanitizePaymentMethodsBrands } from "../../utils/sanitizePaymentMethods";
import type { PaymentMethodsApiResponse } from "../../types";
import type {
  PaymentMethodsProps,
  DealerMethodsPaymentsProps,
} from "store/modules/DealerCare/types.store";
import * as Styled from "./styles";
import MastercardFlag from "assets/flags/mastercard.svg";
import VisaFlag from "assets/flags/visa.svg";
import HipercardFlag from "assets/flags/hipercard.svg";
import CaixaFlag from "assets/flags/caixa.svg";
import EloFlag from "assets/flags/elo.svg";
import AmexFlag from "assets/flags/amex.svg";
import BanrisulFlag from "assets/flags/banrisul.png";
import {
  PaymentMethod,
  MethodId,
  ProviderState,
  ProviderType,
  ProviderCode,
} from "../../enums";
import type { ProcessedProvider } from "../../helpers";
import {
  buildLocalPaymentFormMethods,
  methodIdToApiPayloadValue,
  resolveLocalPaymentCatalog,
  validateLocalPaymentMethods,
  type LocalPaymentFormMethod,
} from "../../utils/localPaymentProviderCatalog";

interface LocalPaymentData {
  id?: string;
  methods: string[];
  debitBrands: string[];
  creditBrands: string[];
}

export interface DealerPaymentDeliveryProps {
  localPaymentData?: LocalPaymentData | null;
  dealerId?: string;
  paymentMethodsFromApi?: PaymentMethodsProps[] | null;
  availableProviders?: ProcessedProvider[];
}

function buildOnDeliveryPayload(
  onDeliveryType: PaymentMethodsProps,
  formMethods: LocalPaymentFormMethod[],
): PaymentMethodsProps {
  const updatedMethods: DealerMethodsPaymentsProps[] =
    onDeliveryType.methods.map((apiMethod) => {
      const formMethod = formMethods.find(
        (fm) =>
          fm.description.toLowerCase() === apiMethod.description.toLowerCase(),
      );
      const enabled = formMethod ? formMethod.enabled : apiMethod.enabled;
      const isCardMethod =
        apiMethod.asksFor === "Brand" && apiMethod.brands?.length;
      let { brands } = apiMethod;
      if (isCardMethod && formMethod?.brands?.length) {
        brands = apiMethod.brands!.map((apiBrand) => {
          const formBrand = formMethod.brands?.find(
            (fb) =>
              fb.description?.toLowerCase() ===
              apiBrand.description?.toLowerCase(),
          );
          return {
            ...apiBrand,
            enabled: formBrand ? formBrand.enabled : apiBrand.enabled,
          };
        });
      }
      return {
        id: apiMethod.id,
        order: apiMethod.order,
        description: apiMethod.description,
        asksFor: apiMethod.asksFor,
        enabled,
        brands: Array.isArray(brands) ? brands : [],
      };
    });
  return {
    id: onDeliveryType.id,
    order: onDeliveryType.order,
    description: onDeliveryType.description,
    methods: updatedMethods,
  };
}

function parsePaymentMethodsFromBillingResponse(
  body: unknown,
): PaymentMethodsProps[] {
  if (Array.isArray(body)) {
    return body;
  }
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    Array.isArray((body as PaymentMethodsApiResponse).data)
  ) {
    return (body as PaymentMethodsApiResponse).data;
  }
  return [];
}

function findOnDeliveryAndInApp(paymentMethods: PaymentMethodsProps[]): {
  onDeliveryType: PaymentMethodsProps | undefined;
  inappType: PaymentMethodsProps | undefined;
} {
  const onDeliveryType = paymentMethods.find(
    (pm) =>
      pm.order === PAYMENT_METHOD_ORDER.DELIVERY ||
      pm.description === "ONDELIVERY",
  );
  const inappType = paymentMethods.find(
    (pm) =>
      pm.order === PAYMENT_METHOD_ORDER.ONLINE || pm.description === "INAPP",
  );
  return { onDeliveryType, inappType };
}

async function resolvePaymentMethodsForBillingSave(
  dealerId: string,
  fromProps: PaymentMethodsProps[] | null | undefined,
): Promise<PaymentMethodsProps[]> {
  const propList = fromProps ?? [];
  const { onDeliveryType, inappType } = findOnDeliveryAndInApp(propList);
  if (onDeliveryType && inappType) {
    return propList;
  }

  const res = await Billing.get(
    `/v2/dealers/${dealerId}/paymentmethods-all-origins`,
  );
  const raw = parsePaymentMethodsFromBillingResponse(res.data);
  return sanitizePaymentMethodsBrands(raw);
}

const LOCAL_PAYMENT_PROVIDER_CONFIG = {
  name: "Pagamento na entrega",
  code: ProviderCode.LocalPayment,
  priority: 0,
  state: ProviderState.Enabled,
  type: ProviderType.LocalPayment,
};

const DealerPaymentDelivery: React.FC<DealerPaymentDeliveryProps> = ({
  localPaymentData,
  dealerId,
  paymentMethodsFromApi,
  availableProviders,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [methods, setMethods] = useState<LocalPaymentFormMethod[]>([]);
  const methodsRef = useRef<LocalPaymentFormMethod[]>([]);

  const paymentCatalog = useMemo(
    () => resolveLocalPaymentCatalog(availableProviders),
    [availableProviders],
  );

  useEffect(() => {
    const initialMethods = buildLocalPaymentFormMethods(
      localPaymentData ?? null,
      paymentCatalog,
    );
    methodsRef.current = initialMethods;
    setMethods(initialMethods);
  }, [localPaymentData, paymentCatalog]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setMethods([...methodsRef.current]);
  };

  const handleMethodToggle = (methodId: string) => {
    setMethods((prev) =>
      prev.map((method) => {
        if (method.id === methodId) {
          const newEnabled = !method.enabled;
          return {
            ...method,
            enabled: newEnabled,
            brands: method.brands?.map((brand) => ({
              ...brand,
              enabled: newEnabled ? brand.enabled : false,
            })),
          };
        }
        return method;
      }),
    );
  };

  const handleBrandToggle = (methodId: string, brandId: string) => {
    setMethods((prev) =>
      prev.map((method) =>
        method.id === methodId
          ? {
              ...method,
              brands: method.brands?.map((brand) =>
                brand.id === brandId
                  ? { ...brand, enabled: !brand.enabled }
                  : brand,
              ),
            }
          : method,
      ),
    );
  };

  const getEnabledBrands = (methodId: string): string[] => {
    const method = methods.find((m) => m.id === methodId);
    return (
      method?.brands
        ?.filter((brand) => brand.enabled)
        .map((brand) => brand.description) || []
    );
  };

  const validatePaymentMethods = (): string | null =>
    validateLocalPaymentMethods(methods);

  const handleSave = async () => {
    if (!dealerId) {
      toast("ID do revendedor não encontrado", { type: "error" });
      return;
    }

    const validationError = validatePaymentMethods();
    if (validationError) {
      toast(
        validationError ||
          "É necessário preencher os campos para ativação do pagamento na entrega.",
        { type: "error" },
      );
      return;
    }

    setIsLoading(true);

    try {
      const paymentMethodsList = await resolvePaymentMethodsForBillingSave(
        dealerId,
        paymentMethodsFromApi,
      );
      const { onDeliveryType, inappType } =
        findOnDeliveryAndInApp(paymentMethodsList);

      if (!onDeliveryType || !inappType) {
        toast(
          "Não foi possível carregar os meios de pagamento (entrega e app) para sincronizar com o faturamento.",
          { type: "error" },
        );
        return;
      }

      const enabledMethods = methods
        .filter((method) => method.enabled)
        .map((method) => methodIdToApiPayloadValue(method.id));

      const creditBrands = getEnabledBrands(MethodId.Credit);
      const debitBrands = getEnabledBrands(MethodId.Debit);
      const payload = {
        ...LOCAL_PAYMENT_PROVIDER_CONFIG,
        methods: enabledMethods,
        acquirer: {
          creditBrands: enabledMethods.includes(PaymentMethod.Credit)
            ? creditBrands
            : [],
          debitBrands: enabledMethods.includes(PaymentMethod.Debit)
            ? debitBrands
            : [],
        },
      };

      if (!localPaymentData?.id) {
        await createProvider({
          merchantId: dealerId,
          data: payload,
        });
      } else {
        await updateProvider({
          merchantId: dealerId,
          providerId: localPaymentData.id,
          data: payload,
        });
      }

      const onDeliveryPayload = buildOnDeliveryPayload(onDeliveryType, methods);
      await Billing.post(`/v2/dealers/${dealerId}/paymentmethods`, [
        onDeliveryPayload,
        inappType,
      ]);

      toast("Alterações aplicadas com sucesso!", {
        type: "success",
      });

      const updatedLocalPaymentData: LocalPaymentData = {
        id: localPaymentData?.id,
        methods: enabledMethods,
        creditBrands: enabledMethods.includes(PaymentMethod.Credit)
          ? creditBrands
          : [],
        debitBrands: enabledMethods.includes(PaymentMethod.Debit)
          ? debitBrands
          : [],
      };

      const updatedMethods = buildLocalPaymentFormMethods(
        updatedLocalPaymentData,
        paymentCatalog,
      );
      methodsRef.current = updatedMethods;
      setMethods(updatedMethods);
      setIsEditing(false);
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        "Houve um erro ao salvar as alterações. Tente novamente!",
      );
      toast(errorMessage, { type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const brandIconMap: Record<string, string> = {
    mastercard: MastercardFlag,
    visa: VisaFlag,
    hipercard: HipercardFlag,
    caixa: CaixaFlag,
    banrisul: BanrisulFlag,
    elo: EloFlag,
    amex: AmexFlag,
  };

  const getBrandIcon = (brandName: string) => {
    const normalizedName = brandName.toLowerCase();
    const iconSrc = brandIconMap[normalizedName];

    if (!iconSrc) return null;

    return (
      <Styled.BrandIconImage key={brandName} src={iconSrc} alt={brandName} />
    );
  };

  const isActivePaymentOnDelivery = methods?.some((method) => method.enabled);
  return (
    <Styled.Container>
      <LoadingSpinner isSpinnerVisible={isLoading} />
      <Styled.Header>
        <Styled.HeaderCaption variant="caption">
          {t("paymentOnDelivery")}
        </Styled.HeaderCaption>
        {isActivePaymentOnDelivery ? (
          <Styled.ChipActive
            label={t("paymentOnDeliveryActive")}
            size="small"
          />
        ) : (
          <Styled.ChipInactive
            label={t("paymentOnDeliveryInactive")}
            size="small"
          />
        )}
      </Styled.Header>

      {!isEditing ? (
        <>
          <Styled.PaymentOptions>
            {methods.map((method) => (
              <Styled.PaymentOption key={method.id}>
                <Styled.PaymentOptionContent>
                  <Styled.CheckCircleIconStyled enabled={method.enabled} />
                  <Styled.MethodDescription enabled={method.enabled}>
                    {method.description}
                  </Styled.MethodDescription>
                </Styled.PaymentOptionContent>
                {method.enabled &&
                  method.brands &&
                  method.brands.length > 0 && (
                    <Styled.CardFlags>
                      {method.brands
                        .filter((brand) => brand.enabled)
                        .map((brand) => getBrandIcon(brand.description))}
                    </Styled.CardFlags>
                  )}
              </Styled.PaymentOption>
            ))}
          </Styled.PaymentOptions>

          <Divider />

          <Styled.ActionsContainer>
            <Styled.EditButton variant="contained" onClick={handleEditClick}>
              {t("edit")}
            </Styled.EditButton>
          </Styled.ActionsContainer>
        </>
      ) : (
        <>
          <Styled.MethodsContainer>
            {methods.map((method, index) => (
              <React.Fragment key={method.id}>
                <Styled.MethodContainer>
                  <FormControlLabel
                    control={
                      <Styled.CheckboxPrimary
                        checked={method.enabled}
                        onChange={() => handleMethodToggle(method.id)}
                      />
                    }
                    label={
                      <Styled.MethodLabel>
                        {method.description}
                      </Styled.MethodLabel>
                    }
                  />

                  {method.enabled &&
                    method.brands &&
                    method.brands.length > 0 && (
                      <Styled.BrandsContainer>
                        <Styled.BrandsTitle variant="caption">
                          Bandeiras Aceitas
                        </Styled.BrandsTitle>
                        <Styled.BrandsList>
                          {method.brands.map((brand) => (
                            <FormControlLabel
                              key={brand.id}
                              control={
                                <Styled.CheckboxPrimarySmall
                                  checked={brand.enabled}
                                  onChange={() =>
                                    handleBrandToggle(method.id, brand.id)
                                  }
                                  size="small"
                                />
                              }
                              label={
                                <Styled.BrandLabel>
                                  {brand.description}
                                </Styled.BrandLabel>
                              }
                            />
                          ))}
                        </Styled.BrandsList>
                      </Styled.BrandsContainer>
                    )}
                </Styled.MethodContainer>
                {index < methods.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Styled.MethodsContainer>

          <Divider />

          <Styled.ActionsContainer>
            <Styled.CancelButton variant="outlined" onClick={handleCancelEdit}>
              Cancelar
            </Styled.CancelButton>
            <Styled.ApplyButton variant="contained" onClick={handleSave}>
              Aplicar
            </Styled.ApplyButton>
          </Styled.ActionsContainer>
        </>
      )}
    </Styled.Container>
  );
};

export default DealerPaymentDelivery;
